import * as React from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { getPlotConfig } from '../utils/graph';
import { transformToPlotlyFormat, validateChartData } from '../utils/chartDataTransformer';
import Plot from 'react-plotly.js';

// Import centralized interfaces
import type { GraphMessageProps } from '../interfaces';

const useStyles = makeStyles({
  graphContainer: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '4px'
  }
});


const GraphMessage: React.FC<GraphMessageProps> = ({ plotData, layout, structuredData }) => {
  const styles = useStyles();
  const config = React.useMemo(() => getPlotConfig(), []);
  const plotRef = React.useRef<any>(null);

  // Transform API structured data to Plotly format if available
  const chartData = React.useMemo(() => {
    // If we have structured data from API, transform it
    if (structuredData && validateChartData(structuredData)) {
      return transformToPlotlyFormat(structuredData);
    }
    
    // Otherwise, use existing plotData and layout (backward compatibility)
    if (plotData && Array.isArray(plotData) && plotData.length > 0) {
      return {
        data: plotData,
        layout: layout || {}
      };
    }
    
    return null;
  }, [plotData, layout, structuredData]);

  const effectiveLayout = React.useMemo(() => {
    if (!chartData) return {};
    
    return {
      autosize: true,
      responsive: true,
      ...chartData.layout
    };
  }, [chartData]);

  React.useEffect(() => {
    const container = document.querySelector(`.${styles.graphContainer}`);
    if (!container) return undefined;

    const ro = new ResizeObserver(() => {
      if (plotRef.current?.el && (window as any).Plotly?.Plots?.resize) {
        (window as any).Plotly.Plots.resize(plotRef.current.el);
      } else {
        window.dispatchEvent(new Event('resize'));
      }
    });

    ro.observe(container);
    return () => ro.disconnect();
  }, [styles.graphContainer]);

  // Show nothing if no valid chart data
  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return null;
  }


  return (
    <div className={styles.graphContainer}>
      <Plot
        ref={plotRef}
        data={chartData.data}
        layout={effectiveLayout as any}
        config={config as any}
        useResizeHandler
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default GraphMessage;

